import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { ShellDomainProvider } from '../utility/shell-domain.provider';
import { FilterObject, LogicalOperatorFilterObject, FilterQueryArray, GQLUpdateFilter, LogicalOperatorNestedFilterObject } from './filter.interface';
import { comparisonOperators, logicalOperators, otherOperators } from './filter.const';
import {
  NestedQueryConfig,
  InsertQueryConfig,
  QueryBasicConfig,
  UpdateQueryConfig,
  NestedItemRemoverConfig,
  DeleteQueryConfig,
  MultiFilterDeleteQueryConfig
} from './models/query-builder.model';


@Injectable({
    providedIn: 'root',
})
export class GqlQueryBuilderService {
    prepareQuery(entityName: string, fields: Array<string | object>, filters: string| Array<any>, orderBy: string = '_id: -1', pageNumber: number = 0, pageSize: number = 15, isExcludeCount?: boolean) {
        let filtersString = '';
        if (Array.isArray(filters)) {
            // search text aka reg search grouped
            const otherFilter = filters.filter(function (filter) {
                return filter.operator !== 'search';
            });
            const groupedFilter = filters.filter(function (filter) {
                return filter.operator === 'search';
            });
            if (Array.isArray(groupedFilter) && groupedFilter.length > 1) {
                const otherFilterQuery = this.prepareFilters(otherFilter);
                const groupedFilterQuery = this.prepareFilters(groupedFilter, ' __or ');
                if (groupedFilterQuery && otherFilterQuery) {
                    filtersString = otherFilterQuery + ' & ( ' + groupedFilterQuery + ' ) ';
                } else if (otherFilterQuery) {
                    filtersString = otherFilterQuery;
                } else {
                    filtersString = groupedFilterQuery;
                }
            } else {
                filtersString = this.prepareFilters(filters);
            }
        } else {
            filtersString = filters ? filters : filtersString;
        }

        return gql`
                query findData{
                 ${entityName}s(
                   Model:{
                     PageNumber: ${pageNumber + 1},
                     Filter: "{ ${filtersString} }",
                     Sort: "{${orderBy}}",
                     PageSize: ${pageSize}
                   }){
                     Data{
                       ${this.queryFieldsMap(fields)}
                     },
                     ErrorMessage,
                     Success,
                     ValidationResult
					 ${isExcludeCount ? '' : 'TotalCount'}
                   }
                }
              `;
    }

    prepareQueryBasic(entityName: string, fields: Array<string | object>, filters: string, orderBy?: string, pageNumber?: number, pageSize?: number) {
        return gql`
                query findData{
                 ${this.buildQueryBasic({
            entityName, fields, filters, orderBy, pageNumber, pageSize
        })}
                }
              `;
    }

    prepareQueryForMultiplePage(entityName: string, fields: Array<string | object>, filters: string| Array<any>, orderBy: string = '_id: -1', pages:Array<number> = [1, 2], pageSize: number = 100) {
        let filtersString = '';
        if (Array.isArray(filters)) {
            // search text aka reg search grouped
            const otherFilter = filters.filter(function (filter) {
                return filter.operator !== 'search';
            });
            const groupedFilter = filters.filter(function (filter) {
                return filter.operator === 'search';
            });
            if (Array.isArray(groupedFilter) && groupedFilter.length > 1) {
                const otherFilterQuery = this.prepareFilters(otherFilter);
                const groupedFilterQuery = this.prepareFilters(groupedFilter, ' __or ');
                if (groupedFilterQuery && otherFilterQuery) {
                    filtersString = otherFilterQuery + ' & ( ' + groupedFilterQuery + ' ) ';
                } else if (otherFilterQuery) {
                    filtersString = otherFilterQuery;
                } else {
                    filtersString = groupedFilterQuery;
                }
            } else {
                filtersString = this.prepareFilters(filters);
            }
        } else {
            filtersString = filters ? filters : filtersString;
        }

        let query = '';
        pages.forEach(page => {
            query += `${entityName}s_${page}:${entityName}s(
            Model:{
            PageNumber: ${page},
            Filter: "{ ${filtersString} }",
            Sort: "{${orderBy}}",
            PageSize: ${pageSize}
            }){
            Data{
                ${this.queryFieldsMap(fields)}
            },
            ErrorMessage,
            Success,
            ValidationResult,
            TotalCount
            }`
        })

        return gql` query find{ ${query}}`;
    }

    buildQueryBasic(config: QueryBasicConfig) {
        return `
            ${config.entityName}s(
            Model:{
             ${config.pageNumber ? `PageNumber: ${config.pageNumber},` : ''}
             Filter: "{ ${config.filters} }"
             ${config.orderBy ? `Sort: ${config.orderBy},` : ''}
             ${config.pageSize ? `PageSize: ${config.pageSize},` : ''}
            }){
              Data{
                ${this.queryFieldsMap(config.fields)}
              },
              ErrorMessage,
              Success,
              ValidationResult,
              TotalCount
            }
        `;
    }

    private queryFieldsMap(fields?: Array<string | object>): string {
        return fields ? fields
            .map(field => typeof field === "object" ? `${Object.keys(field)[0]} { ${this.queryFieldsMap(Object.values(field)[0])} }` : `${field}`)
            .join(", ") : "";
    }

    /**
     * Get distinct data
     * @param entityName
     * @param group
     */

    prepareDistinctQuery(entityName: string, filter: string, group: string, pageNumber: number = 0, pageSize: number = 100, sort?: string, unwind?: string) {
        return gql`
            query distinctQuery {
                ${this.buildDistinctQuery(entityName, filter, group, pageNumber, pageSize, sort, unwind)}
            }`;
    }

    buildDistinctQuery(entityName: string, filter: string, group: string, pageNumber = 0, pageSize = 100, sort?: string, unwind?: string) {
        return `AggregateQuery(
            Model:{
                EntityName: "${entityName}",
                PageNumber: ${pageNumber + 1},
                PageSize: ${pageSize},
                ${filter ? `Filter: "${filter}",` : ''}
                ${unwind ? `Unwind: "${unwind}",` : ''}
                Group: "${group}"
                ${sort ? `, Sort: "${sort}"` : ''}
            }){
             Data,
             ErrorMessage,
             Success,
             ValidationResult,
             TotalCount
           }`;
    }

    prepareDistinctQueryForMultiplePage(entityName: string, filter: string, group, pages = [1, 2], pageSize = 100, sort?: string, unwind?: string) {
        let query = '';
        pages.forEach(page => {
            query += `${entityName}s_${page}: AggregateQuery(
                Model:{
                    EntityName: "${entityName}",
                    PageNumber: ${page},
                    PageSize: ${pageSize},
                    ${filter ? `Filter: "${filter}",` : ''}
                    ${unwind ? `Unwind: "${unwind}",` : ''}
                    Group: "${group}"
                    ${sort ? `, Sort: "${sort}"` : ''}
                }){
                 Data,
                 ErrorMessage,
                 Success,
                 ValidationResult,
                 TotalCount
               }`;
        })

        return gql` query distinctQuery { ${query}}`;
    }

    prepareInsertModel(entityName: string, model: any) {
        return gql`
            mutation create{
                ${this.buildInsertQuery({ entityName, model })}
            }
        `;
    }

    buildInsertQuery(config: InsertQueryConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        return `
            ${config.entityName}(
                Model: ${modelString}, MutationType: INSERT
            ) {
            ErrorMessage
            Result
            Success
            ValidationResult
            }
        `;
    }

    prepareUpdateModel(entityName: string, model: any, entityId: string, filterProperty?: string) {
        if (!filterProperty) filterProperty = null;
        return gql`
            mutation update{
                ${this.buildUpdateQuery({ entityName, model, entityId, filterProperty })}
            }
        `;
    }

    buildUpdateQuery(config: UpdateQueryConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        let filterProperty = '_id';
        if (config && config.filterProperty) {
            filterProperty = config.filterProperty;
        }
        return `
            ${config.entityName}(
                Model: ${modelString},
                MutationType: UPDATE,
                Filter: "{'${filterProperty}':'${config.entityId}'}"
            ) {
            ErrorMessage
            Result
            Success
            ValidationResult
            }
        `;
    }


    /**
     * Insert Item in array in multilevel
     * @param entityName
     * @param model
     * @param entityId
     * @param filter
     * @param pushModal
     * @param setProp
     * @param ArrayFilter  // optional
     */
    prepareInsertNestedArrayModel(entityName: string, model: any, entityId: string, filter: string, pushModal: any, setProp: string, ArrayFilter?: string) {

        return gql`
            mutation insertNested {
                ${this.buildNestedInsertQuery({
            entityName, model, entityId, filter, pushModal, setProp, ArrayFilter
        })}
            }
        `;
    }

    buildNestedInsertQuery(config: NestedQueryConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        const pushQuery = this.convertObjectToGQLStringForUpdate(config.pushModal, '');
        const arrayFilter = config.ArrayFilter ? `, ArrayFilter: "{${config.ArrayFilter}}"` : '';
        return `
            ${config.entityName}(
                Model: ${modelString},
                MutationType: PushUpdate,
                Filter: "{${config.entityId ? `_id:'${config.entityId}'` : ''} ${config.filter ? `${(config.entityId ? ', ' : '') + config.filter}` : ''}}",
                PushQuery: "{${config.setProp}: ${pushQuery}}",
                ${arrayFilter}
            ) {
            ErrorMessage
            Result
            Success
            ValidationResult
            }
        `;
    }

    /**
     * Update Item in array in multilevel
     * @param entityName
     * @param model
     * @param entityId
     * @param filter
     * @param pushModal
     * @param setProp
     * @param ArrayFilter
     */

    prepareNestedArrayUpdateModel(entityName: string, model: any, entityId: string, filter: string, pushModal: any, setProp: string, ArrayFilter: string) {
        return gql`
            mutation updateNested {
                ${this.buildNestedUpdateQuery({
            entityName, model, entityId, filter, pushModal, setProp, ArrayFilter
        })}
            }
        `;
    }

    buildNestedUpdateQuery(config: NestedQueryConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        const updateQuery = this.convertObjectToGQLStringForUpdate(config.pushModal, `${config.setProp}`);
        const arrayFilter = config.ArrayFilter ? `, ArrayFilter: "{arrayFilter:[${config.ArrayFilter}]}"` : '';
        return `
            ${config.entityName}(
                Model: ${modelString},
                MutationType: PushUpdate,
                Filter: "{${config.entityId ? `_id:'${config.entityId}'` : ''} ${config.filter ? `${(config.entityId ? ', ' : '') + config.filter}` : ''}}",
                UpdateQuery: "${updateQuery}"
                ${arrayFilter}
            ) {
            ErrorMessage
            Result
            Success
            ValidationResult
            }
        `;
    }

    /**
     * Update Item in Object in multilevel
     * @param entityName
     * @param model
     * @param entityId
     * @param filter
     * @param pushModal
     * @param setProp
     */
    prepareNestedObjectUpdateModel(entityName: string, model: any, entityId: string, filter: string, pushModal: any, setProp: string) {
        return gql`
            mutation updateNested{
                ${this.buildNestedObjectUpdate({
            entityName, model, entityId, filter, pushModal, setProp
        })}
            }
        `;
    }

    buildNestedObjectUpdate(config: NestedQueryConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        const updateQuery = this.convertObjectToGQLStringForUpdate(config.pushModal, `${config.setProp}`);
        return `
            ${config.entityName}(
                Model: ${modelString},
                MutationType: PushUpdate,
                Filter: "{${config.entityId ? `_id:'${config.entityId}'` : ''} ${config.filter ? `${(config.entityId ? ', ' : '') + config.filter}` : ''}}",
                UpdateQuery: "${updateQuery}"
            ) {
            ErrorMessage
            Result
            Success
            ValidationResult
            }
        `;
    }

    /**
     * remove Item in array in multilevel
     * @param entityName
     * @param model
     * @param entityId
     * @param filter
     * @param updateQuery
     * @param ArrayFilter // optional
     */
    prepareNestedArrayItemRemoveModel(entityName: string, model: any, entityId: string, filter: string, updateQuery: any, ArrayFilter?: string) {

        return gql`
            mutation pullUpdate{
                ${this.buildQueryForArrayItemRemoveModel({
            entityName, model, entityId, filter, updateQuery, ArrayFilter
        })}
            }
        `;
    }

    buildQueryForArrayItemRemoveModel(config: NestedItemRemoverConfig) {
        const modelString = this.convertObjectToGQLString(config.model);
        const arrayFilter = config.ArrayFilter ? `, ArrayFilter: "{${config.ArrayFilter}}"` : '';
        return `
            ${config.entityName}(
            Model: ${modelString},
            MutationType: PULL_UPDATE,
            Filter: "{${config.entityId ? `_id:'${config.entityId}'` : ''} ${config.filter ? `${(config.entityId ? ', ' : '') + config.filter}` : ''}}",
            UpdateQuery: "{${config.updateQuery}}"
            ${arrayFilter}
         ) {
           ErrorMessage
           Result
           Success
           ValidationResult
         }
        `;
    }
    /**
     *
     * @param entityName
     * @param entityId
     */
    prepareDeleteModel(entityName: string, entityId: string, filterProperty?: string) {
        if (!filterProperty) filterProperty = null;
        return gql`
            mutation delete{
             ${this.buildQueryForDelete({ entityName, entityId, filterProperty })}
            }
        `;
    }

    buildQueryForDelete(config: DeleteQueryConfig) {
        let filterProperty = '_id';
        if (config && config.filterProperty) {
            filterProperty = config.filterProperty;
        }
        return `
        ${config.entityName}(
            MutationType: DELETE,
            Filter: "{'${filterProperty}':'${config.entityId}'}"
         ) {
           ErrorMessage
           Result
           Success
           ValidationResult
         }`;
    }

    prepareMultiFilterDeleteModel(entityName: string, entityId: string, filterProperty?: string | Array<{ key: string, value: any }>, payload?: any) {
      if (!filterProperty) filterProperty = null;
      return gql`
              mutation delete{
               ${this.buildQueryForMultiFilterDelete({ entityName, entityId, filterProperty, payload })}
              }
          `;
    }

    buildQueryForMultiFilterDelete(config: MultiFilterDeleteQueryConfig) {
      let filter = null;
      if (config && config.filterProperty && typeof config.filterProperty === 'object') {
        filter = config.filterProperty.reduce((previousValue, currentValue) => {
          const filterValue = `'${currentValue.key}' : '${currentValue.value}'`;
          return previousValue ? `${previousValue} , ${filterValue}` : filterValue;
        }, '');
      }
      const gqlFilter = filter ? `"{${filter}}"` : `"{'${config.filterProperty}':'${config.entityId}'}"`;
      const eventData = config.payload ? this.convertObjectToGQLString(config.payload) : null;
      return eventData ? `
          ${config.entityName}(
              MutationType: DELETE,
              Filter: ${gqlFilter},
              EventData: ${JSON.stringify(eventData)}
           ) {
             ErrorMessage
             Result
             Success
             ValidationResult
           }` : `
          ${config.entityName}(
              MutationType: DELETE,
              Filter: ${gqlFilter}
           ) {
             ErrorMessage
             Result
             Success
             ValidationResult
           }`
    }

    aggregateMultipleMutation(query: any[]) {
        return gql`
            mutation aggregateCall{
             ${query.join('  ')}
            }
        `;
    }

    aggregateMultipleQuery(query: any[]) {
        return gql`
            query aggregateCall{
             ${query.join('  ')}
            }
        `;
    }

    /**
     * For Now No need this function
     *
     */
    aggregateMutationCreateModel(entityName: string, model: any) {
        const modelString = this.convertObjectToGQLString(model);
        const mutationQuery = `mutation insert{
             ${entityName}(
                Model: ${modelString},
                MutationType: INSERT
             ) {
               ErrorMessage
               Result
               Success
               ValidationResult
             }
            }
        `;

        return {
            'Uri': ShellDomainProvider['api'].GQLMutationUri,
            'Verb': 'Post',
            'Payload': JSON.stringify({
                'query': mutationQuery,
                'operationName': 'insert',
                'variables': {}
            }),
            'SuccessIf': '{\"data.' + entityName + '.Success\":\"True\"}'
        };
    }
    /**
     * For Now No need this function
     *
     */
    aggregateMutationUpdateModel(entityName: string, model: any) {
        if (model.ItemId) {
            const itemId = model.ItemId;
            delete model.ItemId; // remove `ItemId` field from model for update call

            const modelString = this.convertObjectToGQLString(model);
            const mutationQuery = `mutation update{
             ${entityName}(
                Model: ${modelString},
                MutationType: UPDATE,
                Filter: "{_id:'${itemId}'}"
             ) {
               ErrorMessage
               Result
               Success
               ValidationResult
             }
            }
        `;

            return {
                'Uri': ShellDomainProvider['api'].GQLMutationUri,
                'Verb': 'Post',
                'Payload': JSON.stringify({
                    'operationName': 'update',
                    'variables': {},
                    'query': mutationQuery,
                }),
                'SuccessIf': '{"data.' + entityName + '.Success":"True"}'
            };
        }
    }

    aggregateMutationCustomerFilterUpdateModel(entityName: string, model: any, updateFilter: GQLUpdateFilter) {
        const key = updateFilter['key'] ? updateFilter['key'] : '_id';
        const value = updateFilter['value'];
        if (model.ItemId) {
            const itemId = model.ItemId;
            delete model.ItemId; // remove `ItemId` field from model for update call
        }
        const modelString = this.convertObjectToGQLString(model);
        const mutationQuery = `mutation update{
             ${entityName}(
                Model: ${modelString},
                MutationType: UPDATE,
                Filter: "{${key}:'${value}'}"
             ) {
               ErrorMessage
               Result
               Success
               ValidationResult
             }
            }
        `;

        return {
            'Uri': ShellDomainProvider['api'].GQLMutationUri,
            'Verb': 'Post',
            'Payload': JSON.stringify({
                'operationName': 'update',
                'variables': {},
                'query': mutationQuery,
            }),
            'SuccessIf': '{"data.' + entityName + '.Success":"True"}'
        };

    }

    aggregateMutationCustomerFilterDeleteModel(entityName: string, updateFilter: GQLUpdateFilter) {
        const key = updateFilter['key'] ? updateFilter['key'] : '_id';
        const value = updateFilter['value'];

        const mutationQuery = `mutation delete{
             ${entityName}(
                MutationType: DELETE,
                Filter: "{${key}:'${value}'}"
             ) {
               ErrorMessage
               Result
               Success
               ValidationResult
             }
            }
        `;

        return {
            'Uri': ShellDomainProvider['api'].GQLMutationUri,
            'Verb': 'Post',
            'Payload': JSON.stringify({
                'operationName': 'delete',
                'variables': {},
                'query': mutationQuery,
            }),
            'SuccessIf': '{"data.' + entityName + '.Success":"True"}'
        };

    }

    aggregateMutationAggregateSumModel(entityName: string, model: any,isExcludeCount:boolean=false) {
        const modelString = this.convertObjectToGQLString(model);
        let TotalCount = isExcludeCount==true?'':'TotalCount';
        const mutationQuery = `query aggregateSum{
            AggregateQuery(
                Model: ${modelString}
             ) {
               Data
               ErrorMessage
               Success
               ValidationResult
               ${TotalCount}
             }
            }
        `;

        return {
            'Uri': ShellDomainProvider['api'].GQLQueryUri,
            'Verb': 'Post',
            'Payload': JSON.stringify({
                'operationName': 'aggregateSum',
                'variables': {},
                'query': mutationQuery
            }),
            'SuccessIf': '{"data.AggregateQuery.Success":"True"}'
        };
    }

    prepareFilters(filters: Array<any>, expressionOperator = null) {
        let preparedFilters = '';
        if (!expressionOperator) {
            expressionOperator = ' & ';
        }
        for (let i = 0, j = 0; i < filters.length; i++) {
            if (!this.isInvalid(filters[i].value)) {
                if (Array.isArray(filters[i].value)) {
                    if (filters[i].value.length < 1) {
                        continue;
                    }
                }
                j++;
                if (j > 1) {
                    preparedFilters += expressionOperator;
                }
                const expression = this.prepareExpression(filters[i]);
                preparedFilters += expression;

            }

        }

        return preparedFilters;
    }

    prepareExpression(expressionObject: any) {
        let preparedExpression = '';
        const operator = expressionObject.operator;
        const property = expressionObject.property;
        const value = expressionObject.value;

        const convertedOperator = {
            '=': `'${value}'`,
            'search': `/${expressionObject.value}/i`,
            'raw': `'${value}'`, // Support for raw mongo filter
            // '|': '__or',
            // '>': '__gt',
            // '>=': '__gte',
            // '&': '__and',
            // '<': '__lt',
            // '<=': '__lte',
            // '!=': '__ne',
            // 'range': '__inc',
            // 'contains': '__in',
            // '!contains': '__nin',
        };

        if (operator !== 'raw') {
            preparedExpression += property;
            preparedExpression += ':';
            preparedExpression += convertedOperator[operator];
        } else {
            preparedExpression = value;
        }

        return preparedExpression;
    }

    isInvalid(value: any) {
        return value === undefined || value === null || value === '';
    }

    /**
     * @desc Converts nested Object to GraphQL string (Work In Progress)
     * @author Shaiful Bappy
     * @param fields
     * @param isRecursive
     */
    convertObjectToGQLString(fields: Array<any> | any, isRecursive: boolean = false) {
        let gqlString = '';
        const tempArray = [];
        if (fields) {
            Object.keys(fields).map(key => {
                if (Array.isArray(fields[key])) {
                    // If value type is `Array`, convert the child value recursively
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `${key}: [${this.convertObjectToGQLString(fields[key], true)}]` : `[${this.convertObjectToGQLString(fields[key], true)}]`
                    );
                } else if ((typeof fields[key] === 'object' && fields[key] !== null)) {
                    // If value type is `Object`, convert the child value recursively
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `${key}: {${this.convertObjectToGQLString(fields[key], true)}}` : `{${this.convertObjectToGQLString(fields[key], true)}}`
                    );

                } else if ((typeof fields[key] === 'boolean') || (typeof fields[key] === 'number')) {
                    // If value type is `Boolean`, do not enclose the value with quote
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `${key}: ${fields[key]}` : `${fields[key]}`
                    );
                } else {
                    // If value type is `String` or `Other`, enclose the value with quote
                    if (fields[key] === null) {
                        tempArray.push(
                            isNaN(parseInt(key, 10)) ? `${key}: ${fields[key]}` : `${fields[key]}`
                        );
                    } else {
                        // Encode double and single quote characters
                        let stringValue = fields[key];
                        stringValue = stringValue?.replace(/\\/g, '\\\\');
                        stringValue = stringValue?.replace(/"/g, '\\"');
                        stringValue = stringValue?.replace(/'/g, "\'");

                        // encode `newline`
                        stringValue = stringValue?.replace(/(?:\r\n|\r|\n)/g, '<br>');

                        tempArray.push(
                            isNaN(parseInt(key, 10)) ? `${key}: "${stringValue}"` : `"${stringValue}"`
                        );
                    }
                }
            });

            gqlString = `{${tempArray.join(',')}}`;
        }

        return isRecursive ? tempArray : gqlString;

        /* Alternative solution */
        // const gqlString = JSON.stringify(fields);
        // const gqlString = gqlString.replace(/"([^(")"]+)":/g,"$1:");
        // return gqlString;
    }

    convertObjectToGQLStringForUpdate(fields: Array<any>| any, propName: string, isRecursive: boolean = false) {
        let gqlString = '';
        const tempArray = [];
        if (fields) {
            Object.keys(fields).map(key => {
                if (Array.isArray(fields[key])) {
                    // If value type is `Array`, convert the child value recursively
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `'${propName}${key}': [${this.convertObjectToGQLStringForUpdate(fields[key], '', true)}]` : `[${this.convertObjectToGQLStringForUpdate(fields[key], '', true)}]`
                    );
                } else if ((typeof fields[key] === 'object' && fields[key] !== null)) {
                    // If value type is `Object`, convert the child value recursively
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `'${propName}${key}': {${this.convertObjectToGQLStringForUpdate(fields[key], '', true)}}` : `{${this.convertObjectToGQLStringForUpdate(fields[key], '', true)}}`
                    );

                } else if ((typeof fields[key] === 'boolean') || (typeof fields[key] === 'number')) {
                    // If value type is `Boolean`, do not enclose the value with quote
                    tempArray.push(
                        isNaN(parseInt(key, 10)) ? `'${propName}${key}': ${fields[key]}` : `${fields[key]}`
                    );
                } else {
                    // If value type is `String` or `Other`, enclose the value with quote
                    if (fields[key] === null) {
                        tempArray.push(
                            isNaN(parseInt(key, 10)) ? `'${propName}${key}': ${fields[key]}` : `${fields[key]}`
                        );
                    } else {
                        // Encode double and single quote characters
                        let stringValue = JSON.stringify(fields[key]);
                        stringValue = stringValue.replace(/\\/g, '\\\\');
                        stringValue = stringValue.replace(/"/g, '\\"');
                        stringValue = stringValue.replace(/'/g, "\'");

                        // encode `newline`
                        stringValue = stringValue.replace(/(?:\r\n|\r|\n)/g, '<br>');

                        tempArray.push(
                            isNaN(parseInt(key, 10)) ? `'${propName}${key}': ${stringValue}` : `${stringValue}`
                        );
                    }
                }
            });

            gqlString = `{${tempArray.join(',')}}`;
        }

        return isRecursive ? tempArray : gqlString;

    }

    private IsJsonString(str: string) {
        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }
        return true;
    }


    /*
        GraphQL filter builder or MongoDb Raw query builer
        * For Simple COMPARISON query builder use ***    prepareGQLFilter()  ***
        * For ONE LEVEL NESTED query builder with LOGICAL operators use ***  prepareGQLNestedFilter()   ***
        * You can use generic ***3*** function to make and concat query of your own if you want or if you have to build more than one level
            nested queries
        * https://docs.mongodb.com/manual/reference/operator/query/  see this for operators only logical and comparison is implemented
        * $exists $type $all $elemMatch etc will not work for now
        * MULTI LEVEL NESTED QUERY BUILED POSSIBLE
        Example 1
        [
            {
                Property:'ClientId',
                Operator:'=', comparison operators
                Value:'SabikAbtahee'
            },
            {
                Property:'Age',
                Operator:'>', comparison operators
                Value:20
            }
        ]

        => { 'ClientId':{$eq:'SabikAbtahee'},'Age':{$gt:20} }

        Example 2
        [
            {
                Property:'ClientId',
                Operator:'=', comparison operators
                Value:'SabikAbtahee'
            },
            {
                Property:'Age',
                Operator:'>', comparison operators
                Value:20
            }
        ],'$or'

        => { $or:[{ 'ClientId':{$eq:'SabikAbtahee'} },{ 'Age':{$gt:20} }] }

        Example 3
        [
    {
      Operator:'$and',
      Filters:[
        {
          Property:'ClientId',
          Operator:'=',
          Value:'123456'
        },
        {
          Property:'Age',
          Operator:'>',
          Value:20
        }
      ]
    },
    {
      Operator:'$or',
      Filters:[
        {
          Property:'Name',
          Operator:'=',
          Value:'Sabik'
        },
        {
          Property:'Name',
          Operator:'=',
          Value:'Abtahee'
        }
      ]
    },
    {
      Operator:'',
      Filters:[
        {
          Property:'IsValid',
          Operator:'=',
          Value:true
        },
      ]
    }
  ]


        => { $and:[{ 'ClientId':{$eq:'123456'} },{ 'Age':{$gt:20} }], $or:[{ 'Name':{$eq:'Sabik'} },{ 'Name':{$eq:'Abtahee'} }], 'IsValid':{$eq:true} }
[
			{
				Operator: '',
				Filters: [
					{
						property: 'IsArchived',
						value: false,
						operator: '='
					},
					{
						property: 'PersonId',
						value: '1231414124124124',
						operator: '='
					}
				]
			},
			{
				Operator: '|',
				Filters: [
					{
						property: 'FinDataStatus',
						value: 'ACTIVE',
						operator: '='
					},
					{
						Operator: '&',
						Filters: [
							{
								property: 'FinDataStatus',
								value: 'INACTIVE',
								operator: '='
							},
							{
								property: 'SourceIdentifier',
								value: [0, 1],
								operator: 'contains'
							}
						]
					}
				]
			}
		]

        => {'IsArchived' : {$eq : false},'PersonId' : {$eq : '1231414124124124'},$or:[{'FinDataStatus' : {$eq : 'ACTIVE'}},{$and:[{'FinDataStatus' : {$eq : 'INACTIVE'}},{'SourceIdentifier' : {$in : [0,1]}}]}]}

    */


    prepareGQLFilter(filters: FilterObject[], extraOperator?: string): string {
        const queryObj = this._getQueryArray(filters);

        const query = `{${queryObj.QueryArray.join(',')}}`;
        const logicalQuery = `${queryObj.LogicalQueryArray.join(',')}`;

        if (extraOperator && logicalOperators[extraOperator]) {
            return `{${logicalOperators[extraOperator]}:[${logicalQuery}]}`;
        }
        else if (extraOperator && otherOperators[extraOperator]) {
            return this._prepareGQLFilterForSpecialOperators(filters, otherOperators[extraOperator]);
        }
        return query;
    }

    prepareGQLNestedFilter(filters: LogicalOperatorFilterObject[]) {
        let complexQueryArray: string[] = [];
        for (let obj of filters) {
            if (logicalOperators[obj.Operator]) {
                complexQueryArray.push(this.prepareGQLFilter(obj.Filters, logicalOperators[obj.Operator]).slice(1, -1));
            }
            else {
                complexQueryArray.push(this.prepareGQLFilter(obj.Filters).slice(1, -1));
            }
        }
        return `{${complexQueryArray.join(',')}}`;
    }

    prepareGQLMultiNestedFilter(filters: LogicalOperatorNestedFilterObject[] | FilterObject[]):string {
        return `{${this.multiNestedRecursive(filters,true)}}`;
    }


    private multiNestedRecursive(filters: LogicalOperatorNestedFilterObject[] | FilterObject[],firstTimeflag:boolean):string {
        let FinalQuery=[];
        for(let obj of filters){
            if(obj['Operator']==otherOperators.$not){
                FinalQuery.push(this._prepareGQLFilterForSpecialOperators(obj['Filters'],otherOperators[obj['Operator']]).slice(1, -1));
            }
            else if(obj['Operator']){
                if(firstTimeflag){
                    FinalQuery.push(`${logicalOperators[obj['Operator']]}:[${this.multiNestedRecursive(obj['Filters'],false)}]`);
                }
                else{
                    FinalQuery.push(`{${logicalOperators[obj['Operator']]}:[${this.multiNestedRecursive(obj['Filters'],false)}]}`);
                }
            }
            else if(obj['Operator'] == ''){
                FinalQuery.push(this.prepareGQLFilter(obj['Filters']).slice(1, -1));
            }
            else{
                FinalQuery.push(`{${this.prepareGQLFilter([obj] as FilterObject[]).slice(1, -1)}}`);
            }
        }
        return `${FinalQuery.join(',')}`;
    }

    private _prepareGQLFilterForSpecialOperators(filters: FilterObject[], specialOperator: string): string {
        let queryArray: string[] = [];
        let query: string;
        if (otherOperators[specialOperator]) {

            for (let obj of filters) {
                if (comparisonOperators[obj.operator]) {
                    let value = this._prepareValueBasedOnType(obj.value);
                    if (comparisonOperators[obj.operator] === '$regex') {
                        value = value.slice(1, -1);
                        queryArray.push(`{'${obj.property}':/${value}/i`);
                    }
                    else if(comparisonOperators[obj.operator] === comparisonOperators['==']){
                        queryArray.push(`'${obj.property}':{ ${otherOperators[specialOperator]} : ${value}}`);
                    }
                    else {
                        queryArray.push(`'${obj.property}':{ ${otherOperators[specialOperator]} : { ${comparisonOperators[obj.operator]} : ${value}}}`);
                    }
                }
            }
        }
        query = `{${queryArray.join(',')}}`;

        return query;
    }

    private _prepareValueBasedOnType(value: any): any {
        return value instanceof Date ? `ISODate('${value.toISOString()}')` : typeof (value) == 'string' ? `'${value}'` : typeof (value) == 'object' ? `${JSON.stringify(value)}` : value;
    }

    private _getQueryArray(filters: FilterObject[]): FilterQueryArray {
        let queryArray: string[] = [];
        let logicalQueryArray: string[] = [];

        for (let obj of filters) {
            if (comparisonOperators[obj.operator]) {

                let value = this._prepareValueBasedOnType(obj.value);

                if (typeof (value) == 'string') {
                    value = value.replace(/"/g, "'");
                }
                if (comparisonOperators[obj.operator] === '$regex') {
                    value = value.slice(1, -1);
                    queryArray.push(`'${obj.property}':/${value}/i`);
                    logicalQueryArray.push(`'${obj.property}' : /${value}/i`);
                }
                else if(comparisonOperators[obj.operator] ===comparisonOperators['=='] ){
                    queryArray.push(`'${obj.property}' :  ${value}`);
                    logicalQueryArray.push(`{'${obj.property}' :  ${value}}`);
                }
                else {
                    queryArray.push(`'${obj.property}' : {${comparisonOperators[obj.operator]} : ${value}}`);
                    logicalQueryArray.push(`{'${obj.property}' : {${comparisonOperators[obj.operator]} : ${value}}}`);
                }
            }
        }

        return {
            QueryArray: queryArray,
            LogicalQueryArray: logicalQueryArray
        };
    }

    prepareGraphQlQuery(entityName: string, fields: Array<string | object>, filters: string = '{}', orderBy: string = '_id: -1', pageNumber: number = 0, pageSize: number = 15, isExcludeCount?: boolean, isFullTextSearch: boolean = false) {
        return gql`
                query findData{
                 ${entityName}s(
                   Model:{
                     IsFullTextSearch: ${isFullTextSearch},
                     PageNumber: ${pageNumber + 1},
                     Filter: "${filters}",
                     Sort: "{${orderBy}}",
                     PageSize: ${pageSize}
                   }){
                     Data{
                       ${this.queryFieldsMap(fields)}
                     },
                     ErrorMessage,
                     Success,
                     ValidationResult
					 ${isExcludeCount ? '' : 'TotalCount'}
                   }
                }
              `;
    }






}
